import { useState, useMemo } from 'react';
import { css } from '@emotion/react';

// ── Styles ──────────────────────────────────────────────────────

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 80px;
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 767px) {
    gap: 80px;
    padding: 0 40px;
  }
`;

const headingStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 64px;
  letter-spacing: -1.28px;
  color: #111110;
  text-align: center;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 48px;
    line-height: 48px;
    letter-spacing: -0.96px;
  }
`;

const controlsStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
`;

/* Desktop: slider left + toggle right, side by side */
const sliderToggleDesktopStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 40px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

/* Tablet/Mobile: toggle on top, slider below, stacked */
const sliderToggleMobileStyles = css`
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const sliderBlockStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  max-width: 663px;

  @media (max-width: 1024px) {
    max-width: 663px;
    width: 100%;
  }

  @media (max-width: 767px) {
    max-width: 100%;
    width: 100%;
  }
`;

const sliderLabelRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: -0.48px;
  color: #111110;
`;

const sliderTrackWrapperStyles = css`
  display: flex;
  align-items: center;
  height: 44px;
  padding: 12px 0;
  width: 100%;
`;

const sliderTrackStyles = css`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 100px;
  background: #b4b4b4;
`;

const sliderInputStyles = css`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 100px;
  outline: none;
  cursor: pointer;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #11110e;
    box-shadow:
      0px 0.5px 4px rgba(0, 0, 0, 0.12),
      0px 6px 13px rgba(0, 0, 0, 0.12);
    cursor: pointer;
    position: relative;
    z-index: 2;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #11110e;
    box-shadow:
      0px 0.5px 4px rgba(0, 0, 0, 0.12),
      0px 6px 13px rgba(0, 0, 0, 0.12);
    border: none;
    cursor: pointer;
  }
`;

const toggleWrapperStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #e1e1db;
  border-radius: 50px;
  padding: 4px;
  height: 48px;
  flex-shrink: 0;
`;

const toggleDesktopPositionStyles = css`
  ${toggleWrapperStyles};
  align-self: flex-start;
  margin-top: 18px;
`;

const toggleActiveStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 50px;
  background: #f8f8f6;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #111110;
  cursor: pointer;
  border: none;
  white-space: nowrap;
`;

const toggleBonusStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: #1a5ed1;
`;

const toggleInactiveStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 100px;
  background: none;
  border: none;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #353533;
  cursor: pointer;
  white-space: nowrap;
`;

const plansGridStyles = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(5, auto);
  gap: 24px;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(10, auto);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }
`;

const planCardBaseStyles = css`
  display: grid;
  grid-row: span 5;
  grid-template-rows: subgrid;
  row-gap: 32px;
  padding: 48px 24px 24px;
  border-radius: 16px;
  box-shadow: 0px 15px 44px rgba(0, 0, 0, 0.1);
  position: relative;

  @media (max-width: 767px) {
    grid-row: auto;
    grid-template-rows: none;
  }
`;

const planCardLightStyles = css`
  ${planCardBaseStyles};
  background: #f8f8f5;
`;

const planCardDarkStyles = css`
  ${planCardBaseStyles};
  background: #111110;
`;

const planNameStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 20px;
  letter-spacing: -0.4px;
  margin: 0;
`;

const planDescStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  margin: 0;
  margin-top: -24px;
`;

const planDescCompactStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  margin: 0;
  margin-top: -24px;
`;

const priceStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 40px;
  line-height: 40px;
  letter-spacing: -0.8px;
  margin: 0;
`;

const pricePerStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
`;

const priceSubStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  margin: 0;
`;

const ctaBaseStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 50px;
  border: none;
  padding: 0 24px;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  width: 100%;
`;

const ctaDarkStyles = css`
  ${ctaBaseStyles};
  background: #111110;
  color: #f0f0eb;
`;

const ctaLightStyles = css`
  ${ctaBaseStyles};
  background: #f8f8f5;
  color: #111110;
`;

const featureListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const featureHeaderStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

const featureRowStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  svg {
    margin-top: 3px;
  }
`;

const featureTextStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

const featureTextCompactStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  margin: 0;
`;

const badgeOuterStyles = css`
  position: absolute;
  top: 16px;
  right: 15px;
  background: #242422;
  backdrop-filter: blur(25px);
  border-radius: 4px;
  padding: 4px 8px;
  overflow: hidden;
`;

const badgeTextStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  background: linear-gradient(to right, #ddebff, #4081f4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// ── Icons ───────────────────────────────────────────────────────

function CheckIcon({ light }: { light: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      css={css`
        flex-shrink: 0;
      `}
    >
      <path
        d="M2.5 8.5L6 12L13.5 4.5"
        stroke={light ? 'rgba(240,240,235,0.6)' : 'rgba(17,17,16,0.65)'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      css={css`
        flex-shrink: 0;
      `}
    >
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="rgba(17,17,16,0.65)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarIcon({ light }: { light: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 17 18"
      fill="none"
      css={css`
        flex-shrink: 0;
        margin-top: 1px;
      `}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5002 1C8.79198 1.00011 9.05795 1.16992 9.18086 1.43457L11.2707 5.93555L16.1984 6.5332C16.488 6.56841 16.7312 6.76854 16.8215 7.0459C16.9116 7.3234 16.833 7.62842 16.6193 7.82715L12.9836 11.2061L13.9387 16.0781C13.9946 16.3643 13.8796 16.6576 13.6438 16.8291C13.4078 17.0005 13.0931 17.0195 12.8381 16.8779L8.5002 14.4639L4.16231 16.8779C3.90736 17.0196 3.59266 17.0005 3.35664 16.8291C3.12069 16.6576 3.00572 16.3644 3.06172 16.0781L4.01582 11.2061L0.381057 7.82715C0.167296 7.62841 0.0887128 7.32349 0.178908 7.0459C0.269214 7.7685 0.512318 6.56831 0.801955 6.5332L5.72871 5.93555L7.81953 1.43457C7.9425 1.16989 8.20834 1 8.5002 1ZM6.91426 6.94629C6.80509 7.18137 6.58166 7.34356 6.32442 7.375L2.58516 7.82715L5.34395 10.3926C5.53359 10.5692 5.61839 10.8316 5.56856 11.0859L4.84395 14.7803L8.13594 12.9502L8.22285 12.9092C8.43038 12.8266 8.66629 12.8401 8.86446 12.9502L12.1555 14.7803L11.4318 11.0859C11.382 10.8315 11.4667 10.5692 11.6564 10.3926L14.4143 7.82715L10.676 7.375C10.4187 7.34365 10.1954 7.1814 10.0861 6.94629L8.5002 3.53027L6.91426 6.94629Z"
        fill={light ? '#6da3ff' : '#1a5ed1'}
      />
    </svg>
  );
}

// ── Pricing tiers data ──────────────────────────────────────────

const SUBSCRIBER_TIERS = [
  { max: 500, label: '500', prices: { free: 0, lite: 19, pro: 25, everything: 49 } },
  { max: 1000, label: '1,000', prices: { free: 0, lite: 19, pro: 25, everything: 49 } },
  { max: 2500, label: '2,500', prices: { free: 0, lite: 29, pro: 35, everything: 59 } },
  { max: 5000, label: '5,000', prices: { free: 0, lite: 39, pro: 49, everything: 79 } },
  { max: 10000, label: '10,000', prices: { free: 0, lite: 59, pro: 69, everything: 99 } },
  { max: 25000, label: '25,000', prices: { free: 0, lite: 99, pro: 119, everything: 149 } },
  { max: 50000, label: '50,000', prices: { free: 0, lite: 0, pro: 159, everything: 199 } },
];

// ── Shared sub-components ───────────────────────────────────────

type ToggleProps = {
  billingCycle: 'annual' | 'monthly';
  onToggle: (cycle: 'annual' | 'monthly') => void;
  wrapperCss: ReturnType<typeof css>;
};

function BillingToggle({ billingCycle, onToggle, wrapperCss }: ToggleProps) {
  return (
    <div css={wrapperCss}>
      <button
        css={billingCycle === 'annual' ? toggleActiveStyles : toggleInactiveStyles}
        type="button"
        onClick={() => onToggle('annual')}
      >
        Annual
        {billingCycle === 'annual' && <span css={toggleBonusStyles}>(1 month free)</span>}
      </button>
      <button
        css={billingCycle === 'monthly' ? toggleActiveStyles : toggleInactiveStyles}
        type="button"
        onClick={() => onToggle('monthly')}
      >
        Monthly
      </button>
    </div>
  );
}

type SliderProps = {
  tierIndex: number;
  subscriberLabel: string;
  sliderPercent: number;
  maxIndex: number;
  onChange: (index: number) => void;
};

function SubscriberSlider({
  tierIndex,
  subscriberLabel,
  sliderPercent,
  maxIndex,
  onChange,
}: SliderProps) {
  return (
    <div css={sliderBlockStyles}>
      <div css={sliderLabelRowStyles}>
        <span>How many subscribers?</span>
        <span>{subscriberLabel}</span>
      </div>
      <div css={sliderTrackWrapperStyles}>
        <div css={sliderTrackStyles}>
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              height: 4px;
              border-radius: 100px;
              background: #100101;
              width: ${sliderPercent}%;
            `}
          />
          <input
            css={sliderInputStyles}
            type="range"
            min={0}
            max={maxIndex}
            value={tierIndex}
            onChange={e => onChange(Number(e.target.value))}
          />
        </div>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────

type PricingPlansProps = {
  compact: boolean;
  altStyle: boolean;
  cardGap: number;
  darkListText: boolean;
};

export default function PricingPlans({
  compact,
  altStyle,
  cardGap,
  darkListText,
}: PricingPlansProps) {
  const [tierIndex, setTierIndex] = useState(1);
  const [billingCycle, setBillingCycle] = useState<'annual' | 'monthly'>('annual');

  const currentTier = SUBSCRIBER_TIERS[tierIndex];

  const prices = useMemo(() => {
    const base = currentTier.prices;
    if (billingCycle === 'annual') return base;
    return {
      free: 0,
      lite: base.lite ? Math.round(base.lite * 1.2) : 0,
      pro: base.pro ? Math.round(base.pro * 1.2) : 0,
      everything: base.everything ? Math.round(base.everything * 1.2) : 0,
    };
  }, [currentTier, billingCycle]);

  const subscriberLabel = currentTier.label;
  const sliderPercent = (tierIndex / (SUBSCRIBER_TIERS.length - 1)) * 100;
  const maxIndex = SUBSCRIBER_TIERS.length - 1;

  const lightText = 'rgba(17, 17, 16, 0.65)';
  const lightListText = darkListText ? 'rgb(17, 17, 16)' : 'rgba(17, 17, 16, 0.65)';
  const darkText = 'rgba(240, 240, 235, 0.6)';

  const descCss = compact ? planDescCompactStyles : planDescStyles;
  const featureCss = compact ? featureTextCompactStyles : featureTextStyles;

  const lightCardCss = altStyle
    ? css`
        ${planCardBaseStyles};
        background: #f0f0eb;
        box-shadow: none;
      `
    : planCardLightStyles;

  const darkCardCss = altStyle
    ? css`
        ${planCardBaseStyles};
        background: #111110;
        box-shadow: none;
      `
    : planCardDarkStyles;

  return (
    <section css={sectionStyles}>
      <h1 css={headingStyles}>Pricing plans</h1>

      <div css={controlsStyles}>
        {/* Desktop layout: slider left + toggle right */}
        <div css={sliderToggleDesktopStyles}>
          <SubscriberSlider
            tierIndex={tierIndex}
            subscriberLabel={subscriberLabel}
            sliderPercent={sliderPercent}
            maxIndex={maxIndex}
            onChange={setTierIndex}
          />
          <BillingToggle
            billingCycle={billingCycle}
            onToggle={setBillingCycle}
            wrapperCss={toggleDesktopPositionStyles}
          />
        </div>

        {/* Tablet/Mobile layout: toggle on top, slider below */}
        <div css={sliderToggleMobileStyles}>
          <BillingToggle
            billingCycle={billingCycle}
            onToggle={setBillingCycle}
            wrapperCss={toggleWrapperStyles}
          />
          <SubscriberSlider
            tierIndex={tierIndex}
            subscriberLabel={subscriberLabel}
            sliderPercent={sliderPercent}
            maxIndex={maxIndex}
            onChange={setTierIndex}
          />
        </div>

        {/* Plan cards */}
        <div
          css={css`
            ${plansGridStyles};
            gap: ${cardGap}px;
          `}
        >
          {/* Free */}
          <div css={lightCardCss}>
            <p
              css={css`
                ${planNameStyles};
                color: #111110;
              `}
            >
              Free
            </p>
            <p
              css={css`
                ${descCss};
                color: ${lightText};
              `}
            >
              Tools to grow your list
            </p>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <span
                  css={css`
                    ${priceStyles};
                    color: #111110;
                  `}
                >
                  $0
                </span>
                <span
                  css={css`
                    ${pricePerStyles};
                    color: #111110;
                  `}
                >
                  /mo
                </span>
              </div>
              <p
                css={css`
                  ${priceSubStyles};
                  color: ${lightText};
                  opacity: 0;
                `}
              >
                For up to {subscriberLabel} subscribers
              </p>
            </div>
            <button css={ctaDarkStyles} type="button">
              Start now
            </button>
            <div css={featureListStyles}>
              <p
                css={css`
                  ${featureHeaderStyles};
                  color: ${lightListText};
                `}
              >
                Your intro to Flodesk:
              </p>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Forms &amp; landing pages
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Designer templates
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Segmentation
                </p>
              </div>
              <div css={featureRowStyles}>
                <CrossIcon />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Email sending
                </p>
              </div>
            </div>
          </div>

          {/* Lite */}
          <div css={lightCardCss}>
            <p
              css={css`
                ${planNameStyles};
                color: #111110;
              `}
            >
              Lite
            </p>
            <p
              css={css`
                ${descCss};
                color: ${lightText};
              `}
            >
              Everything you need to start
            </p>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <span
                  css={css`
                    ${priceStyles};
                    color: #111110;
                  `}
                >
                  ${prices.lite}
                </span>
                <span
                  css={css`
                    ${pricePerStyles};
                    color: #111110;
                  `}
                >
                  /mo
                </span>
              </div>
              <p
                css={css`
                  ${priceSubStyles};
                  color: ${lightText};
                `}
              >
                For up to {subscriberLabel} subscribers
              </p>
            </div>
            <button css={ctaDarkStyles} type="button">
              Start free
            </button>
            <div css={featureListStyles}>
              <p
                css={css`
                  ${featureHeaderStyles};
                  color: ${lightListText};
                `}
              >
                Everything in Free, plus:
              </p>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Unlimited email sends
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Email analytics
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Max of 25,000 subscribers
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  1 seat
                </p>
              </div>
              <div css={featureRowStyles}>
                <StarIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: #1a5ed1;
                  `}
                >
                  Limited time bonus: 1 workflow
                </p>
              </div>
            </div>
          </div>

          {/* Pro (dark card, featured) */}
          <div css={darkCardCss}>
            <div css={badgeOuterStyles}>
              <span css={badgeTextStyles}>Most popular</span>
            </div>
            <p
              css={css`
                ${planNameStyles};
                color: #f0f0eb;
              `}
            >
              Pro
            </p>
            <p
              css={css`
                ${descCss};
                color: ${darkText};
              `}
            >
              Advanced tools, made simple
            </p>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <span
                  css={css`
                    ${priceStyles};
                    color: #f0f0eb;
                  `}
                >
                  ${prices.pro}
                </span>
                <span
                  css={css`
                    ${pricePerStyles};
                    color: #f0f0eb;
                  `}
                >
                  /mo
                </span>
              </div>
              <p
                css={css`
                  ${priceSubStyles};
                  color: ${darkText};
                `}
              >
                For up to {subscriberLabel} subscribers
              </p>
            </div>
            <button css={ctaLightStyles} type="button">
              Start free
            </button>
            <div css={featureListStyles}>
              <p
                css={css`
                  ${featureHeaderStyles};
                  color: ${darkText};
                `}
              >
                Everything in Lite, plus:
              </p>
              <div css={featureRowStyles}>
                <CheckIcon light />
                <p
                  css={css`
                    ${featureCss};
                    color: ${darkText};
                  `}
                >
                  Unlimited workflows
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light />
                <p
                  css={css`
                    ${featureCss};
                    color: ${darkText};
                  `}
                >
                  Advanced analytics &amp; reporting
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light />
                <p
                  css={css`
                    ${featureCss};
                    color: ${darkText};
                  `}
                >
                  Brand customization
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light />
                <p
                  css={css`
                    ${featureCss};
                    color: ${darkText};
                  `}
                >
                  2 seats
                </p>
              </div>
              <div css={featureRowStyles}>
                <StarIcon light />
                <p
                  css={css`
                    ${featureCss};
                    color: #6da3ff;
                  `}
                >
                  Limited time bonus: 1 checkout
                </p>
              </div>
            </div>
          </div>

          {/* Everything */}
          <div css={lightCardCss}>
            <p
              css={css`
                ${planNameStyles};
                color: #111110;
              `}
            >
              Everything
            </p>
            <p
              css={css`
                ${descCss};
                color: ${lightText};
              `}
            >
              Email meets e-commerce
            </p>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <span
                  css={css`
                    ${priceStyles};
                    color: #111110;
                  `}
                >
                  ${prices.everything}
                </span>
                <span
                  css={css`
                    ${pricePerStyles};
                    color: #111110;
                  `}
                >
                  /mo
                </span>
              </div>
              <p
                css={css`
                  ${priceSubStyles};
                  color: ${lightText};
                `}
              >
                For up to {subscriberLabel} subscribers
              </p>
            </div>
            <button css={ctaDarkStyles} type="button">
              Start free
            </button>
            <div css={featureListStyles}>
              <p
                css={css`
                  ${featureHeaderStyles};
                  color: ${lightListText};
                `}
              >
                Everything in Pro, plus:
              </p>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Unlimited integrated checkouts
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Sales pages &amp; funnels
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Subscriptions &amp; payment plans
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  Abandoned cart automations
                </p>
              </div>
              <div css={featureRowStyles}>
                <CheckIcon light={false} />
                <p
                  css={css`
                    ${featureCss};
                    color: ${lightListText};
                  `}
                >
                  3 seats
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
